import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Users,
  Baby,
  Heart,
  Utensils,
  UserCheck,
  HandHeart,
  Church,
  Gem,
  ExternalLink,
  MessageCircle,
  Clock,
  CheckCircle,
  Loader2,
  Bell,
  ChevronRight,
  Dumbbell,
  Music,
  Sparkles,
  Crown,
  BookOpen,
  Briefcase,
  Coffee,
  GraduationCap,
  Gift
} from 'lucide-react';
import BottomNav from '../components/BottomNav';
import { groupsAPI, notificationsAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import notificationSound from '../utils/notificationSound';

// Icon map for ministry groups by ID and also by name (for fallback matching)
const GROUP_CONFIG = {
  'group-smbc-main': { icon: Church, color: 'bg-navy-900', name: 'Second Missionary Baptist Church' },
  'group-children': { icon: Baby, color: 'bg-pink-500', name: "Children's Ministry" },
  'group-youth': { icon: Users, color: 'bg-purple-500', name: 'Youth Ministry' },
  'group-usher': { icon: UserCheck, color: 'bg-blue-500', name: 'Usher Ministry' },
  'group-feeding': { icon: Utensils, color: 'bg-orange-500', name: 'Feeding Ministry' },
  'group-wecare': { icon: HandHeart, color: 'bg-red-500', name: 'We Care Ministry' },
  'group-ministers': { icon: BookOpen, color: 'bg-indigo-500', name: 'Ministers Ministry' },
  'group-marriage': { icon: Gem, color: 'bg-rose-500', name: 'Marriage Ministry' },
  'group-fitness': { icon: Dumbbell, color: 'bg-green-500', name: 'Fitness Ministry' },
  'group-faith-fitness': { icon: Dumbbell, color: 'bg-emerald-500', name: 'Faith and Fitness' },
  'group-voices-of-faith': { icon: Music, color: 'bg-violet-500', name: 'Voices of Faith Choir' },
  'group-youth-choir': { icon: Music, color: 'bg-fuchsia-500', name: 'Youth Choir' },
  'group-praise-dance': { icon: Sparkles, color: 'bg-amber-500', name: 'Praise Dance Ministry' },
  'group-brotherhood': { icon: Briefcase, color: 'bg-slate-600', name: 'Brotherhood Ministry' },
  'group-womens-fellowship': { icon: Coffee, color: 'bg-pink-400', name: "Women's Fellowship Ministry" },
  'group-sunday-school': { icon: BookOpen, color: 'bg-cyan-500', name: 'Sunday School' },
  'group-operation-christmas-child': { icon: Gift, color: 'bg-red-500', name: 'Operation Christmas Child' },
};

// Helper to get icon and color for a group by ID or name
const getGroupConfig = (group) => {
  // First try to match by ID
  if (GROUP_CONFIG[group.id]) {
    return GROUP_CONFIG[group.id];
  }
  
  // Fallback: match by name (case-insensitive partial match)
  const groupNameLower = group.name?.toLowerCase() || '';
  
  if (groupNameLower.includes('second missionary') || groupNameLower.includes('smbc')) {
    return { icon: Church, color: 'bg-navy-900' };
  }
  if (groupNameLower.includes('children')) {
    return { icon: Baby, color: 'bg-pink-500' };
  }
  if (groupNameLower.includes('youth choir')) {
    return { icon: Music, color: 'bg-fuchsia-500' };
  }
  if (groupNameLower.includes('youth')) {
    return { icon: Users, color: 'bg-purple-500' };
  }
  if (groupNameLower.includes('usher')) {
    return { icon: UserCheck, color: 'bg-blue-500' };
  }
  if (groupNameLower.includes('feeding')) {
    return { icon: Utensils, color: 'bg-orange-500' };
  }
  if (groupNameLower.includes('we care') || groupNameLower.includes('wecare')) {
    return { icon: HandHeart, color: 'bg-red-500' };
  }
  if (groupNameLower.includes('minister')) {
    return { icon: BookOpen, color: 'bg-indigo-500' };
  }
  if (groupNameLower.includes('marriage')) {
    return { icon: Gem, color: 'bg-rose-500' };
  }
  if (groupNameLower.includes('faith and fitness') || groupNameLower.includes('faith & fitness')) {
    return { icon: Dumbbell, color: 'bg-emerald-500' };
  }
  if (groupNameLower.includes('fitness')) {
    return { icon: Dumbbell, color: 'bg-green-500' };
  }
  if (groupNameLower.includes('voices of faith') || groupNameLower.includes('choir')) {
    return { icon: Music, color: 'bg-violet-500' };
  }
  if (groupNameLower.includes('praise dance') || groupNameLower.includes('dance')) {
    return { icon: Sparkles, color: 'bg-amber-500' };
  }
  if (groupNameLower.includes('brotherhood')) {
    return { icon: Briefcase, color: 'bg-slate-600' };
  }
  if (groupNameLower.includes('women') || groupNameLower.includes('fellowship')) {
    return { icon: Coffee, color: 'bg-pink-400' };
  }
  if (groupNameLower.includes('sunday school')) {
    return { icon: BookOpen, color: 'bg-cyan-500' };
  }
  if (groupNameLower.includes('christmas child') || groupNameLower.includes('operation christmas')) {
    return { icon: Gift, color: 'bg-red-500' };
  }
  
  // Default fallback
  return { icon: Users, color: 'bg-gray-500' };
};

// Check if a group is the main SMBC group
const isMainGroup = (group) => {
  const nameLower = group.name?.toLowerCase() || '';
  return group.id === 'group-smbc-main' || 
         nameLower.includes('second missionary') || 
         nameLower.includes('smbc');
};

const MinistryTeamsPage = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joiningGroup, setJoiningGroup] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const navigate = useNavigate();
  const { user } = useAuth();
  const prevUnreadTotal = React.useRef(0);
  const isFirstLoad = React.useRef(true);

  useEffect(() => {
    loadGroups();
    loadUnreadCounts();
    
    // Poll for updates every 30 seconds
    const pollInterval = setInterval(() => {
      loadUnreadCounts();
    }, 30000);
    
    return () => clearInterval(pollInterval);
  }, []);

  const loadGroups = async () => {
    try {
      const data = await groupsAPI.getAll();
      setGroups(data);
    } catch (error) {
      console.error('Error loading groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCounts = async () => {
    try {
      const data = await notificationsAPI.getUnreadCount();
      const counts = data.groupUnread || {};
      const newTotal = Object.values(counts).reduce((a, b) => a + b, 0);
      
      // Play sound if new join requests came in (not on first load)
      if (!isFirstLoad.current && newTotal > prevUnreadTotal.current) {
        notificationSound.playJoinRequestSound();
      }
      prevUnreadTotal.current = newTotal;
      isFirstLoad.current = false;
      
      setUnreadCounts(counts);
    } catch (error) {
      console.error('Error loading unread counts:', error);
    }
  };

  const handleJoinRequest = async (groupId) => {
    setJoiningGroup(groupId);
    try {
      await groupsAPI.requestJoin(groupId);
      await loadGroups(); // Refresh to show pending status
    } catch (error) {
      console.error('Error requesting to join:', error);
      alert(error.response?.data?.detail || 'Failed to submit join request');
    } finally {
      setJoiningGroup(null);
    }
  };

  const handleOpenGroup = (group) => {
    if (group.membershipStatus === 'approved' || group.membershipStatus === 'leader' || group.isLeader) {
      navigate(`/group-chat/${group.id}`);
    }
  };

  const handleOpenExternalLink = (e, link) => {
    e.stopPropagation();
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  const getStatusBadge = (group) => {
    if (group.isLeader || group.membershipStatus === 'leader') {
      return (
        <Badge className="bg-purple-100 text-purple-700 text-xs">
          Leader
        </Badge>
      );
    }
    if (group.membershipStatus === 'approved') {
      return (
        <Badge className="bg-green-100 text-green-700 text-xs flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          Member
        </Badge>
      );
    }
    if (group.membershipStatus === 'pending') {
      return (
        <Badge className="bg-yellow-100 text-yellow-700 text-xs flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Pending
        </Badge>
      );
    }
    return null;
  };

  const getActionButton = (group) => {
    if (group.isLeader || group.membershipStatus === 'leader' || group.membershipStatus === 'approved') {
      return (
        <Button
          size="sm"
          className="bg-navy-900 hover:bg-navy-800 text-white"
          onClick={() => handleOpenGroup(group)}
        >
          <MessageCircle className="w-4 h-4 mr-1" />
          Chat
        </Button>
      );
    }
    if (group.membershipStatus === 'pending') {
      return (
        <Button size="sm" disabled className="bg-gray-200 text-gray-500">
          <Clock className="w-4 h-4 mr-1" />
          Awaiting
        </Button>
      );
    }
    return (
      <Button
        size="sm"
        className="bg-tan-500 hover:bg-tan-600 text-white"
        onClick={() => handleJoinRequest(group.id)}
        disabled={joiningGroup === group.id}
      >
        {joiningGroup === group.id ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <>Join</>
        )}
      </Button>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-navy-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-br from-navy-900 to-navy-800 text-white px-5 pt-8 pb-8 rounded-b-3xl shadow-xl">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center backdrop-blur-sm">
              <Users className="w-6 h-6 text-tan-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Ministry Groups</h1>
              <p className="text-tan-400 text-sm font-medium">Connect with your ministry</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/10"
            onClick={() => navigate('/notifications')}
            data-testid="notifications-btn"
          >
            <Bell className="w-6 h-6" />
          </Button>
        </div>
      </div>

      <div className="px-4 -mt-4 space-y-3">
        {/* Info Card */}
        <Card className="border-0 shadow-lg rounded-2xl overflow-hidden bg-tan-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-navy-900 flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-tan-400" />
              </div>
              <div className="text-sm text-gray-700">
                <p className="font-medium text-navy-900 mb-1">In-App Ministry Groups</p>
                <p>Request to join a ministry and connect with your team through our built-in messaging.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Ministry Groups List */}
        {(() => {
          // Separate the main SMBC group from others
          const mainGroup = groups.find(g => isMainGroup(g));
          const otherGroups = groups.filter(g => !isMainGroup(g));
          
          // Render function for a group card
          const renderGroupCard = (group, isHighlighted = false) => {
            const config = getGroupConfig(group);
            const Icon = config.icon;
            const iconColor = config.color;
            const unreadCount = unreadCounts[group.id] || 0;
            const canOpen = group.isLeader || group.membershipStatus === 'leader' || group.membershipStatus === 'approved';
            
            return (
              <Card 
                key={group.id}
                className={`border-0 shadow-lg rounded-2xl overflow-hidden transition-all ${
                  canOpen ? 'cursor-pointer hover:shadow-xl active:scale-[0.98]' : ''
                } ${isHighlighted ? 'ring-2 ring-tan-500 bg-gradient-to-r from-tan-50 to-white' : ''}`}
                onClick={() => canOpen && handleOpenGroup(group)}
                data-testid={`group-card-${group.id}`}
              >
                <CardContent className="p-4">
                  {isHighlighted && (
                    <div className="flex items-center gap-2 mb-3">
                      <Crown className="w-4 h-4 text-tan-600" />
                      <span className="text-xs font-semibold text-tan-700 uppercase tracking-wide">Main Church Group</span>
                    </div>
                  )}
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full ${iconColor} flex items-center justify-center relative ${isHighlighted ? 'ring-2 ring-tan-400 ring-offset-2' : ''}`}>
                      <Icon className="w-6 h-6 text-white" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center font-bold">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className={`font-bold truncate ${isHighlighted ? 'text-navy-900 text-lg' : 'text-navy-900'}`}>{group.name}</h3>
                        {getStatusBadge(group)}
                      </div>
                      <p className="text-sm text-gray-500 truncate">{group.description}</p>
                      <p className="text-xs text-gray-400 mt-1">Led by {group.leaderName}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {getActionButton(group)}
                      {group.externalLink && (
                        <button
                          onClick={(e) => handleOpenExternalLink(e, group.externalLink)}
                          className="text-xs text-blue-600 flex items-center gap-1 hover:underline"
                          data-testid={`external-link-${group.id}`}
                        >
                          <ExternalLink className="w-3 h-3" />
                          GroupMe
                        </button>
                      )}
                    </div>
                  </div>
                  {canOpen && (
                    <div className="flex items-center justify-end mt-2 text-gray-400">
                      <span className="text-xs">Tap to chat</span>
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          };
          
          return (
            <>
              {/* Main SMBC Group - Highlighted at top */}
              {mainGroup && renderGroupCard(mainGroup, true)}
              
              {/* Other Ministry Groups */}
              {otherGroups.map((group) => renderGroupCard(group, false))}
            </>
          );
        })()}

        {groups.length === 0 && (
          <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
            <CardContent className="p-8 text-center">
              <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No ministry groups available yet.</p>
            </CardContent>
          </Card>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default MinistryTeamsPage;
